import { normalizePath } from '@/api/accessMutations'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { DeploymentType, getDeploymentType } from '@/utils/deployment'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

const ANALYTICS_ENABLED_STORAGE_KEY = 'isAnalyticsEnabled'

function isAnalyticsEnabled(): boolean {
	try {
		const runtimeToggle = localStorage.getItem(ANALYTICS_ENABLED_STORAGE_KEY)
		if (runtimeToggle !== null) {
			return runtimeToggle === 'true'
		}
	} catch {
		// falls back to the deployment type below
	}

	// Off by default on a developer's own machine, on everywhere real traffic lands.
	return getDeploymentType() !== DeploymentType.LOCAL
}

let initialized = false

export function initAnalytics(): void {
	if (initialized || !isAnalyticsEnabled()) {
		return
	}

	const key = import.meta.env.VITE_POSTHOG_KEY
	if (!key) {
		return
	}

	posthog.init(key, {
		api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
		// The router drives pageviews explicitly, so each carries its matched route id.
		capture_pageview: false,
		// Avoid a person profile for traffic that never logs in.
		person_profiles: 'identified_only',
		// Autocapture keeps the element chain and data-testid, so clicks stay queryable, and drops the
		// clicked text, which on a device row is a serial or a metadata value.
		mask_all_text: true,
		// Replays keep layout, clicks and navigation. Every input and all text are masked, since the
		// text on most screens is a customer's device data. Recording itself is a project setting.
		session_recording: { maskAllInputs: true, maskTextSelector: '*' },
	})
	registerContext()
	// The replay is found from Sentry by this id: a tag on error events, an attribute on spans.
	posthog.onSessionId(sessionId => {
		Sentry.setTag('session_id', sessionId)
		Sentry.setAttribute('session_id', sessionId)
	})

	initialized = true
}

/**
 * The release and host under the same keys Sentry carries, on every event. `posthog.reset()` wipes
 * registered properties, so this runs after every reset too.
 */
function registerContext(): void {
	posthog.register({ release: import.meta.env.VITE_RELEASE, host: window.location.hostname })
}

/** Email is the only stable key the `/me` payloads carry, so it is the distinct id. */
export function identifyUser(email: string): void {
	if (!initialized) {
		return
	}
	posthog.identify(email, { email })
}

export function resetAnalytics(): void {
	if (!initialized) {
		return
	}
	posthog.reset()
	registerContext()
}

/** Groups drive PostHog's own analytics; the flat id rides on every event under the Sentry tag's key. */
const GROUP_ID_KEYS = { organization: 'organization_id', project: 'tenant_id' } as const

function setGroup(type: keyof typeof GROUP_ID_KEYS, key: string, name: string | undefined): void {
	if (!initialized) {
		return
	}
	if (name) {
		posthog.group(type, key, { name })
	} else {
		posthog.group(type, key)
	}
	posthog.register({ [GROUP_ID_KEYS[type]]: key })
}

export function setOrganizationGroup(organizationId: string, name?: string): void {
	setGroup('organization', organizationId, name)
}

export function setProjectGroup(tenantId: string, name?: string): void {
	setGroup('project', tenantId, name)
}

/** posthog-js has no per type ungroup, so the project key is dropped from the registered groups. */
export function clearProjectGroup(): void {
	if (!initialized) {
		return
	}
	const { project: _project, ...groups } = posthog.getGroups()
	posthog.register({ $groups: groups })
	posthog.unregister(GROUP_ID_KEYS.project)
}

/** Query endpoints that take their filters in a POST body. */
const READ_OVER_POST_TAILS = [
	'/search',
	'/ids',
	'/filter-options',
	'/timeseries',
	'/timeseries-sampled',
	'/fetch-data',
	'/aggregate-value',
	'/histogram',
	'/latency-histogram',
	'/device-pulse',
	'/stream-status',
	'/locate-devices-historical',
	'/sessions',
	'/sessions/dashboard',
	'/project-audit-logs',
	'/audit-logs',
	'/session-rules',
	'/subscriber-groups',
	'/notification-history',
	'/notification-failures',
	'/dbcs/dashboard',
	'/diff',
	'/counts',
]

const isReadOverPost = (path: string): boolean =>
	READ_OVER_POST_TAILS.some(tail => path.endsWith(tail)) || /\/actions\/[^/]+\/devices$/.test(path)

/**
 * One event per successful write, named by endpoint, so feature use is measurable without a capture
 * call at every mutation site. Numeric ids and tenancy slugs collapse, so one path is one feature.
 * ponytail: named keys (streams, action types, metadata keys) stay in the path; add a rule here when
 * one shows up as noise in PostHog.
 */
export function trackApiWrite(method: string | undefined, url: string | undefined): void {
	if (!initialized || !method || !url) {
		return
	}
	const verb = method.toLowerCase()
	if (verb === 'get' || verb === 'head' || verb === 'options') {
		return
	}
	const path = normalizePath(url)
	if (verb === 'post' && isReadOverPost(path)) {
		return
	}
	posthog.capture('api_write', {
		method: verb.toUpperCase(),
		path: path
			.replace(/\/(organizations|tenants)\/[^/]+/g, '/$1/:id')
			.replace(/\/(\d+|[0-9a-f]{8}-[0-9a-f-]{27})(?=\/|$)/gi, '/:id'),
	})
}

/** The route rides on every later event too, clicks and writes included, as it does on Sentry's. */
export function trackPageView(route: string | undefined): void {
	if (!initialized) {
		return
	}
	posthog.register({ route })
	posthog.capture('$pageview')
}

/**
 * Tags every Sentry error with the PostHog person and recording URLs, so an error and a replay are
 * joinable. Inert until PostHog is initialized. Exceptions stay with Sentry, PostHog gets no copy.
 */
export function analyticsSentryIntegration() {
	return posthog.sentryIntegration({ sendExceptionsToPostHog: false })
}

/**
 * Keeps PostHog's identity, organization group and project group in step with the auth store for
 * the life of the app, the same two step shape `startSentryContextSync` uses: apply once
 * immediately for a rehydrated session, then again on every later change.
 */
export function startAnalyticsContextSync(): () => void {
	type User = ReturnType<typeof useAuthStore.getState>['user']
	// A page load with no user is not a logout: resetting there would mint a new anonymous person
	// and session on every visit to the login page.
	const applyUser = (user: User, previousUser?: User) => {
		if (!user) {
			if (previousUser) {
				resetAnalytics()
			}
			return
		}
		identifyUser(user.email)
	}

	const applyOrganizationId = (organizationId: string | null) => {
		if (organizationId) {
			const organization = useAuthStore.getState().user?.organizations?.find(o => o.id === organizationId)
			setOrganizationGroup(organizationId, organization?.name)
		}
	}

	const applyTenantId = (tenantId: string) => {
		if (!tenantId) {
			clearProjectGroup()
			return
		}
		const user = useAuthStore.getState().user
		const tenant = isPlatformUser(user) ? user.tenant_list?.find(t => t.id === tenantId) : undefined
		setProjectGroup(tenantId, tenant?.name)
	}

	const initialState = useAuthStore.getState()
	applyUser(initialState.user)
	applyOrganizationId(initialState.currentOrganizationId)
	applyTenantId(initialState.currentTenant)

	const stops = [
		useAuthStore.subscribe(state => state.user, applyUser),
		useAuthStore.subscribe(state => state.currentOrganizationId, applyOrganizationId),
		useAuthStore.subscribe(state => state.currentTenant, applyTenantId),
	]

	return () => {
		for (const stop of stops) {
			stop()
		}
	}
}
